import React, { FC, ReactNode, PropsWithChildren } from 'react';
import classNames from 'classnames';
import View from '../view';
import Popup, { IPopupProps } from '../popup';
import Button, { IButtonType } from '../button';

interface IModalProps extends Omit<IPopupProps, 'onClose' | 'duration' | 'position' | 'round' | 'closeIcon' | 'closeable'> {
  /**
   * 头部 css class
   */
  headerClassName?: string;
  /**
   * 内容区域 css class
   */
  bodyClassName?: string;
  /**
   * 页脚 css class
   */
  footerClassName?: string;
  /**
   * 标题
   */
  title?: ReactNode;
  /**
   * 取消按钮文字，如不需取消按钮，可传入 null
   * @default 取消
   */
  cancelText?: string | null;
  /**
   * 确认按钮文字，如不需确认按钮，可传入 null
   * @default 确认
   */
  okText?: string | null;
  /**
   * 确定按钮类型，参考 Button 组件的 type 属性（基于视觉效果需要，将按钮的 plain 设为 true, 并去除了边框）
   * @default primary
   */
  okButtonType?: IButtonType;
  /**
   * 自定义页脚（footer 属性有值时，okText cancelText onOK onCancel 无效）
   */
  footer?: ReactNode;
  /**
   * 模态框关闭时的回调
   */
  onClose?: () => void;
  /**
   * 点击取消按钮时的回调
   */
  onCancel?: () => void;
  /**
   * 点击确定按钮时的回调
   */
  onOK?: () => void;
}

const Modal: FC<PropsWithChildren<IModalProps>> = ({
  children,
  className = '',
  headerClassName = '',
  bodyClassName = '',
  footerClassName = '',
  title,
  cancelText = '取消',
  okText = '确定',
  okButtonType = 'primary',
  footer,
  onCancel,
  onOK,
  ...restProps
}) => {
  const rootClass = classNames('ink-modal', className);
  const headerClass = classNames('ink-modal__header', headerClassName);
  const contentClass = classNames('ink-modal__content', bodyClassName);
  const footerClass = classNames(
    'ink-modal__footer',
    {
      'ink-modal__footer--null': !footer,
    },
    footerClassName,
  );

  const isButtonText = okText || cancelText;
  const isRenderAction = isButtonText || footer;

  const selfOnOK = () => {
    if (onOK && typeof onOK === 'function') {
      onOK();
    }
  };

  const selfOnCancel = () => {
    if (onCancel && typeof onCancel === 'function') {
      onCancel();
    }
  };

  return (
    <Popup {...restProps} round className={rootClass}>
      {title && <View className={headerClass}>{title}</View>}
      <View className={contentClass}>{children}</View>
      {isRenderAction && (
        <View className={footerClass}>
          {footer ||
            (isButtonText && (
              <View className="ink-modal__action">
                {cancelText && (
                  <Button className="ink-modal__action__button" plain onClick={selfOnCancel}>
                    {cancelText}
                  </Button>
                )}
                {okText && (
                  <Button className="ink-modal__action__button" type={okButtonType} plain onClick={selfOnOK}>
                    {okText}
                  </Button>
                )}
              </View>
            ))}
        </View>
      )}
    </Popup>
  );
};

export default Modal;
